import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { fmtMoney } from "../../lib/format";
import { roleLabel } from "../../lib/memberManagement";
import OwnerBadge from "../OwnerBadge";

/**
 * Phase 2D.3B.3 — „Активни оферти по служител".
 *
 * CURRENT STATE: who is carrying what right now. One row per (responsible
 * person, team); the row with no responsible person is the unassigned backlog and
 * is always last. Independent of the selected period by design.
 */
export default function DashboardWorkload({ rows, loading, onRowClick }) {
  const { t } = useTranslation();
  const displayRows = useMemo(() => rows || [], [rows]);
  const maxOpen = useMemo(
    () => Math.max(1, ...displayRows.map((r) => r.open_count || 0)),
    [displayRows],
  );
  const anyMixed = displayRows.some((r) => r.mixed_currency);

  if (loading) {
    return <div className="qw-skel h-280 stretch" aria-busy="true" />;
  }
  if (!displayRows.length) {
    return (
      <div className="qw-empty" data-testid="workload-empty">
        {t("dashboard.workload_empty")}
      </div>
    );
  }

  return (
    <div>
      <div
        className="qw-table-wrap"
        tabIndex={0}
        role="region"
        aria-label={t("dashboard.section_workload")}
      >
        <table className="qw-table qw-workload-table" data-testid="workload-table">
          <thead>
            <tr>
              <th>{t("dashboard.workload_column_employee")}</th>
              <th>{t("dashboard.workload_column_role")}</th>
              <th>{t("dashboard.workload_column_team")}</th>
              <th style={{ textAlign: "right" }}>{t("dashboard.workload_column_open")}</th>
              <th style={{ textAlign: "right" }}>{t("dashboard.workload_column_active_value")}</th>
              <th style={{ textAlign: "right" }}>{t("dashboard.workload_column_awaiting")}</th>
              <th style={{ textAlign: "right" }}>{t("dashboard.workload_column_expiring")}</th>
            </tr>
          </thead>
          <tbody>
            {displayRows.map((r) => {
              const key = `${r.owner_user_id || "unassigned"}-${r.team_id || "no-team"}`;
              const width = Math.max(4, Math.round(((r.open_count || 0) / maxOpen) * 100));
              return (
                <tr
                  key={key}
                  data-clickable="true"
                  onClick={() => onRowClick(r)}
                  data-testid={`workload-row-${key}`}
                >
                  <td>
                    {r.owner ? (
                      <OwnerBadge owner={r.owner} small />
                    ) : (
                      <span className="qw-owner-pill unassigned sm">
                        {t("dashboard.workload_unassigned")}
                      </span>
                    )}
                  </td>
                  <td>
                    <span className="qw-role-chip">
                      {r.role ? roleLabel(r.role) : "—"}
                    </span>
                  </td>
                  <td>
                    {r.team_id ? (
                      <span className="qw-team-name">{r.team_name || "—"}</span>
                    ) : (
                      <span className="qw-team-name no-team">
                        {t("dashboard.workload_no_team")}
                      </span>
                    )}
                  </td>
                  <td className="num">
                    <div className="qw-team-cell">
                      <span>{(r.open_count || 0).toLocaleString("bg-BG")}</span>
                      <span className="qw-mini-bar">
                        <span className="fill" style={{ width: `${width}%` }} />
                      </span>
                    </div>
                  </td>
                  <td className="num">
                    {fmtMoney(r.active_value, "EUR")}
                    {r.mixed_currency && (
                      <span
                        className="qw-by-owner-mixed"
                        title={t("dashboard.mixed_currency_note")}
                        data-testid={`workload-mixed-${key}`}
                      >
                        ⚠
                      </span>
                    )}
                  </td>
                  <td className="num">{(r.awaiting_count || 0).toLocaleString("bg-BG")}</td>
                  <td className="num">{(r.expiring_7_count || 0).toLocaleString("bg-BG")}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="qw-by-owner-disclaimer">
        {t("dashboard.workload_note")}
        {anyMixed && (
          <span style={{ display: "block", color: "#b45309", marginTop: 4 }}>
            {t("dashboard.mixed_currency_note")}
          </span>
        )}
      </div>
    </div>
  );
}
